import {
  BlinkIdSingleSideRecognizerResult,
  DisplayableQuad,
  IdBarcodeRecognizerResult,
  RecognizerResultState,
} from '@microblink/blinkid-in-browser-sdk';
import { Point } from '@microblink/blinkid-in-browser-sdk/types/MicroblinkSDK/Geometry';
import { MrzResult } from '@microblink/blinkid-in-browser-sdk/types/Recognizers/BlinkID/MRTD/MrtdStructures';
import { useRef, useState } from 'react';
import { useAsyncEffect } from 'use-async-effect';

import EventsClient from 'lib/clients/EventsClient';
import FrameTriggerClient from 'lib/clients/FrameTriggerClient';
import LivePhotoCaptureClient, {
  IMAGE_FORMAT,
  IMAGE_QUALITY,
} from 'lib/clients/LivePhotoCaptureClient';
import {
  countryToString,
  documentTypeToString,
  MicroblinkClient,
  regionToString,
} from 'lib/clients/MicroblinkClient';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { GovernmentIdSide } from 'lib/interfaces/verificationConfig';
import { CameraInspectionData } from 'verify/lib/interfaces/cameraInspection';
import { calculateGlareRatio } from 'verify/lib/utils/image';
import { bufferHorizontalImage } from 'verify/lib/utils/webcam';
import { CaptureMethod, ClientExtraction } from 'verify/models/verification';
import BaseScanner from 'verify/scanners/BaseScanner/BaseScanner';
import {
  DETECT_INTERVAL,
  GovernmentIdMicroblinkTriggerParams,
  GovernmentIdMicroblinkTriggerType,
  hasValidMrz,
  notCentered,
  PROCESSING_TRIGGER,
  tooClose,
  tooFar,
  TRIGGER_CONFIGS_BY_SIDE,
} from 'verify/scanners/GovernmentIdMicroblinkScanner/client';

const DEFAULT_MANUAL_CAPTURE_DELAY = 8000;
interface GovernmentIdMicroblinkScannerProps {
  delayUntilManualCaptureMs?: number;
  handleCapture: (
    imageDataUris: string[],
    captureMethod: CaptureMethod,
    cameraInspectionData: CameraInspectionData,
    clientExtractions?: ClientExtraction[],
    clientExtractionSource?: string,
  ) => Promise<void>;
  handleError: (err: Error | string, state: 'load' | 'capture') => void;
  labelTakePhoto: string;
  overlay: JSX.Element;
  videoConstraints: MediaTrackConstraints;
  requiresSide?: GovernmentIdSide;
  governmentIdClass?: GovernmentIdClass;
  zoomFactor: number;
}

const GovernmentIdMicroblinkScanner = ({
  /**
   * Manual capture was always enabled for GovernmentIdMicroblinkScanner before this prop was implemented.
   */
  delayUntilManualCaptureMs = DEFAULT_MANUAL_CAPTURE_DELAY,
  handleCapture,
  labelTakePhoto,
  overlay,
  videoConstraints,
  requiresSide,
  governmentIdClass,
  handleError,
  zoomFactor,
}: GovernmentIdMicroblinkScannerProps) => {
  let initializationTime: number | undefined = undefined;

  const isCaptured = useRef(false);
  const [isSlowProcessing, setIsSlowProcessing] = useState(false);

  useAsyncEffect(
    async () => {
      try {
        const mountTime = performance.now();
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        await MicroblinkClient.initialize(requiresSide, governmentIdClass);
        initializationTime = performance.now();
        EventsClient.logAmplitude('government-id.microblink.initialization', {
          initializationTime: (initializationTime - mountTime) / 1000,
          side: requiresSide,
          idClass: governmentIdClass,
        });
      } catch (err) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        handleError(err, 'load');
      }
    },
    () => {
      MicroblinkClient.tearDown();
      initializationTime = undefined;
    },
    [requiresSide],
  );

  const paramsFromVideoFrame = async (
    videoElement: HTMLVideoElement,
    canvasBuffer: HTMLCanvasElement,
    _captureClient: LivePhotoCaptureClient,
    triggerClient: FrameTriggerClient<
      GovernmentIdMicroblinkTriggerType,
      GovernmentIdMicroblinkTriggerParams
    >,
  ): Promise<GovernmentIdMicroblinkTriggerParams> => {
    bufferHorizontalImage({ source: videoElement, precreatedCanvas: canvasBuffer, zoomFactor });

    const videoWidth = videoElement.videoWidth / zoomFactor;
    const videoHeight = videoElement.videoHeight / zoomFactor;
    try {
      const imageDataUri = canvasBuffer.toDataURL(IMAGE_FORMAT, IMAGE_QUALITY);
      const start = performance.now();
      let isProcessing = true;
      setIsSlowProcessing(false);
      setTimeout(() => {
        if (isProcessing) {
          setIsSlowProcessing(true);
        }
      }, 1000);
      const processedImage = await MicroblinkClient.processImage(
        canvasBuffer,
        (supportedClassification: boolean) => {
          if (supportedClassification) {
            setTimeout(() => {
              if (isProcessing && requiresSide === GovernmentIdSide.Front) {
                const end = performance.now();
                const timing = end - start;
                handleMicroblinkCapture(
                  {
                    currentTrigger: triggerClient.getTriggered().getType(),
                    detectedQuad: null,
                    microblinkResult: null,
                    glareRatio: calculateGlareRatio(canvasBuffer),
                    videoWidth,
                    videoHeight,
                    imageDataUri,
                    hasGlare: null,
                    processingTime: timing,
                  },
                  CaptureMethod.Auto,
                  null,
                );
                isProcessing = false;
              }
            }, 1000);
          }
        },
      );

      isProcessing = false;
      const microblinkRecognizerName = processedImage?.recognizerName;

      if (requiresSide === GovernmentIdSide.Front) {
        const microblinkResult =
          processedImage?.recognitionResult as BlinkIdSingleSideRecognizerResult;

        const detectedQuad = processedImage?.detectedQuad;
        const hasGlare = processedImage?.hasGlare ?? null;
        const end = performance.now();
        const timing = end - start;

        const params = {
          currentTrigger: triggerClient.getTriggered().getType(),
          detectedQuad: detectedQuad ?? null,
          microblinkResult,
          microblinkRecognizerName,
          glareRatio: calculateGlareRatio(canvasBuffer),
          videoWidth,
          videoHeight,
          hasGlare,
          imageDataUri,
          processingTime: timing,
        };
        return params;
      } else if (
        requiresSide === GovernmentIdSide.BarcodePdf417 ||
        requiresSide === GovernmentIdSide.Back
      ) {
        const microblinkResult = processedImage?.recognitionResult as IdBarcodeRecognizerResult;
        const detectedQuad = processedImage?.detectedQuad ?? null;
        const hasGlare = processedImage?.hasGlare ?? null;
        const end = performance.now();
        const timing = end - start;
        const params = {
          currentTrigger: triggerClient.getTriggered().getType(),
          detectedQuad: detectedQuad,
          microblinkResult,
          microblinkRecognizerName,
          glareRatio: calculateGlareRatio(canvasBuffer),
          videoWidth,
          videoHeight,
          hasGlare,
          imageDataUri,
          processingTime: timing,
        };
        return params;
      }
    } catch (err) {
      // Here, we stop tick'ing but do nothing else because they can manually take a photo.
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleError(err, 'capture');
    }
    return {
      currentTrigger: triggerClient.getTriggered().getType(),
      glareRatio: calculateGlareRatio(canvasBuffer),
      videoWidth,
      videoHeight,
      microblinkRecognizerName: null,
      detectedQuad: null,
      microblinkResult: null,
      hasGlare: null,
      imageDataUri: null,
      processingTime: null,
    };
  };

  const shouldSubmit = (
    params: GovernmentIdMicroblinkTriggerParams,
    _triggerClient: FrameTriggerClient<
      GovernmentIdMicroblinkTriggerType,
      GovernmentIdMicroblinkTriggerParams
    >,
  ) => {
    // We manually handle the submission to ensure we only submit successfully parsed results
    return (
      params.microblinkResult?.state === RecognizerResultState.Valid &&
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      !tooFar(requiresSide, params) &&
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      !tooClose(requiresSide, params) &&
      !notCentered(params) &&
      hasValidMrz(governmentIdClass, params)
    );
  };

  const normalizePoint = (point: Point, videoWidth: number, videoHeight: number) => {
    return {
      x: point.x / videoWidth,
      y: point.y / videoHeight,
    };
  };

  const normalizeQuad = (quad: DisplayableQuad, videoWidth: number, videoHeight: number) => {
    return {
      bottomLeft: normalizePoint(quad.bottomLeft, videoWidth, videoHeight),
      bottomRight: normalizePoint(quad.bottomRight, videoWidth, videoHeight),
      topLeft: normalizePoint(quad.topLeft, videoWidth, videoHeight),
      topRight: normalizePoint(quad.topRight, videoWidth, videoHeight),
    };
  };

  const handleMicroblinkCapture = async (
    params: GovernmentIdMicroblinkTriggerParams,
    captureMethod: CaptureMethod,
    cameraInspectionData: CameraInspectionData | null,
  ) => {
    if (isCaptured.current) {
      return;
    }
    const clientExtractions = [];
    if (requiresSide === GovernmentIdSide.BarcodePdf417 || requiresSide === GovernmentIdSide.Back) {
      const microblinkResult = params.microblinkResult as IdBarcodeRecognizerResult;
      if (microblinkResult) {
        clientExtractions.push({
          type: 'pdf417',
          value: microblinkResult.barcodeData?.stringData,
        });
        if (params.detectedQuad) {
          const normalizedQuad = normalizeQuad(
            params.detectedQuad,
            params.videoWidth,
            params.videoHeight,
          );
          clientExtractions.push({
            type: 'card',
            value: {
              barcode: {
                'top-left': normalizedQuad.topLeft,
                'top-right': normalizedQuad.topRight,
                'bottom-left': normalizedQuad.bottomLeft,
                'bottom-right': normalizedQuad.bottomRight,
              },
            },
          });
        }
      }
    } else if (requiresSide === GovernmentIdSide.Front) {
      const microblinkResult = params.microblinkResult as BlinkIdSingleSideRecognizerResult;
      if (microblinkResult) {
        clientExtractions.push({
          type: 'viz',
          value: {
            'name-first': microblinkResult.firstName,
            'name-last': microblinkResult.lastName,
            'identification-number': microblinkResult.documentNumber,
            address: microblinkResult.address,
            birthdate: microblinkResult.dateOfBirth.originalString,
            'expiration-date': microblinkResult.dateOfExpiry.originalString,
          },
        });
        if (governmentIdClass === 'pp') {
          const microblinkMrz = microblinkResult.mrz as MrzResult;
          clientExtractions.push({
            type: 'mrz',
            value: microblinkMrz.rawMRZString,
          });
        }
        if (params.detectedQuad) {
          const normalizedQuad = normalizeQuad(
            params.detectedQuad,
            params.videoWidth,
            params.videoHeight,
          );
          clientExtractions.push({
            type: 'card',
            value: {
              front: {
                'top-left': normalizedQuad?.topLeft,
                'top-right': normalizedQuad?.topRight,
                'bottom-left': normalizedQuad?.bottomLeft,
                'bottom-right': normalizedQuad?.bottomRight,
              },
            },
          });
        }
        clientExtractions.push({
          type: 'classification',
          value: {
            type: microblinkResult.classInfo.documentType
              ? documentTypeToString(microblinkResult.classInfo.documentType)
              : null,
            country: microblinkResult.classInfo.country
              ? countryToString(microblinkResult.classInfo.country)
              : null,
            region: microblinkResult.classInfo.region
              ? regionToString(microblinkResult.classInfo.region)
              : null,
          },
        });
      }
    }
    EventsClient.logAmplitude('government-id.microblink.capture', {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      processingTime: params.processingTime / 1000,
      captureTime:
        initializationTime != null ? (performance.now() - initializationTime) / 1000 : undefined,
      side: requiresSide,
      idClass: governmentIdClass,
      captureMethod,
      parsedViz: clientExtractions.find((e) => e.type === 'viz') != null,
      parsedMrz: clientExtractions.find((e) => e.type === 'mrz') != null,
      parsedCard: clientExtractions.find((e) => e.type === 'card') != null,
      parsedClassification: clientExtractions.find((e) => e.type === 'classification') != null,
      parsedPdf417: clientExtractions.find((e) => e.type === 'pdf417') != null,
    });
    await handleCapture(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      [params.imageDataUri],
      captureMethod,
      cameraInspectionData,
      clientExtractions,
      params.microblinkRecognizerName,
    );
    isCaptured.current = true;
  };

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const triggerConfigs = TRIGGER_CONFIGS_BY_SIDE[requiresSide];

  return (
    <BaseScanner<GovernmentIdMicroblinkTriggerType, GovernmentIdMicroblinkTriggerParams>
      handleCapture={({ captureMethod, params, cameraInspectionData }) =>
        handleMicroblinkCapture(params, captureMethod, cameraInspectionData)
      }
      handleFailed={handleError}
      detectInterval={DETECT_INTERVAL}
      labelTakePhoto={labelTakePhoto}
      videoConstraints={videoConstraints}
      triggerConfigs={triggerConfigs}
      paramsFromVideoFrame={paramsFromVideoFrame}
      shouldSubmit={shouldSubmit}
      overlay={overlay}
      delayUntilManualCaptureMs={delayUntilManualCaptureMs}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      triggerOverride={isSlowProcessing ? PROCESSING_TRIGGER : null}
      zoomFactor={zoomFactor}
      scannerType='governmentId'
      disableCaptureClient
    />
  );
};

export default GovernmentIdMicroblinkScanner;
